import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import API_CONFIG from '../../config/api';

interface Guide {
  _id: string;
  title: string;
  content: string;
  tab: 'general' | 'test-request' | 'test-results' | 'remaining-payment';
  active: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

const Guide: React.FC = () => {
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showGuideForm, setShowGuideForm] = useState(false);
  const [editingGuide, setEditingGuide] = useState<string | null>(null);

  const [newGuide, setNewGuide] = useState({
    title: '',
    content: '',
    tab: 'general' as 'general' | 'test-request' | 'test-results' | 'remaining-payment',
    active: true,
    order: 0
  });

  // Fetch guides from API
  const fetchGuides = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');

      const response = await fetch(API_CONFIG.getUrl('guides'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('خطا در دریافت راهنماها');
      }

      const data = await response.json();
      setGuides(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در دریافت راهنماها');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGuides();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (editingGuide) {
      // Update the guide being edited
      setGuides(guides.map(guide => 
        guide._id === editingGuide 
          ? { 
              ...guide, 
              [name]: name === 'active' ? value === 'true' : 
                      name === 'order' ? parseInt(value) || 0 : value 
            }
          : guide
      ));
    } else {
      // Update the new guide
      setNewGuide({
        ...newGuide,
        [name]: name === 'active' ? value === 'true' : 
                name === 'order' ? parseInt(value) || 0 : value
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('authToken');
      const currentGuide = editingGuide 
        ? guides.find(guide => guide._id === editingGuide)
        : newGuide;

      if (!currentGuide) return;

      if (currentGuide.title.trim() === '' || currentGuide.content.trim() === '') {
        alert('لطفاً عنوان و محتوا را وارد کنید.');
        return;
      }

      const url = editingGuide ? API_CONFIG.getUrl(`guides/${editingGuide}`) : API_CONFIG.getUrl('guides');
      const method = editingGuide ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(currentGuide)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'خطا در ذخیره راهنما');
      }

      // Refresh guides list
      await fetchGuides();
      
      // Reset form
      setEditingGuide(null);
      setShowGuideForm(false);
      setNewGuide({
        title: '',
        content: '',
        tab: 'general',
        active: true,
        order: 0
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در ذخیره راهنما');
    }
  };

  const startEditing = (id: string) => {
    setEditingGuide(id);
    setShowGuideForm(true);
  };

  const cancelEditing = () => {
    setEditingGuide(null);
    setShowGuideForm(false);
    setNewGuide({
      title: '',
      content: '',
      tab: 'general',
      active: true,
      order: 0
    });
    // Refresh to get original data
    fetchGuides();
  };

  const toggleGuideStatus = async (id: string) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(API_CONFIG.getUrl(`guides/${id}/toggle`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('خطا در تغییر وضعیت راهنما');
      }

      await fetchGuides();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در تغییر وضعیت راهنما');
    }
  };

  const deleteGuide = async (id: string) => {
    if (!window.confirm('آیا از حذف این راهنما اطمینان دارید؟')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(API_CONFIG.getUrl(`guides/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('خطا در حذف راهنما');
      }

      await fetchGuides();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در حذف راهنما');
    }
  };

  const getTabLabel = (tab: string) => {
    switch (tab) {
      case 'general': return 'راهنمای کلی';
      case 'test-request': return 'پک راهنمای ثبت درخواست آزمایش';
      case 'test-results': return 'پک راهنمای دریافت تصویر جواب آزمایش';
      case 'remaining-payment': return 'پرداخت باقی مانده';
      default: return tab;
    }
  };

  const currentGuide = editingGuide 
    ? guides.find(guide => guide._id === editingGuide)
    : newGuide;

  if (loading) {
    return (
    
        <div className="p-4">
          <div className="flex justify-center items-center h-64">
            <div className="text-lg">در حال بارگذاری...</div>
          </div>
        </div>
 
    );
  }

  return (
   
      <div className="p-4">
        <div className="card bg-white rounded-sm p-4 border border-slate-300">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-slate-800">مدیریت راهنماها</h1>
            <button 
              onClick={() => {
                setEditingGuide(null);
                setShowGuideForm(!showGuideForm);
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {showGuideForm && !editingGuide ? 'انصراف' : 'افزودن راهنمای جدید'}
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
          
          {showGuideForm && (
            <div className="mb-8 bg-gray-50 p-6 rounded-lg">
              <h2 className="text-lg font-semibold mb-4">
                {editingGuide ? 'ویرایش راهنما' : 'افزودن راهنمای جدید'}
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">عنوان راهنما</label>
                    <input
                      type="text"
                      name="title"
                      value={currentGuide?.title || ''}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">تب</label>
                    <select
                      name="tab"
                      value={currentGuide?.tab || 'general'}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="general">راهنمای کلی</option>
                      <option value="test-request">پک راهنمای ثبت درخواست آزمایش</option>
                      <option value="test-results">پک راهنمای دریافت تصویر جواب آزمایش</option>
                      <option value="remaining-payment">پرداخت باقی مانده</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ترتیب نمایش</label>
                    <input
                      type="number"
                      name="order"
                      value={currentGuide?.order || 0}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">وضعیت</label>
                    <select
                      name="active"
                      value={currentGuide?.active ? 'true' : 'false'}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="true">فعال</option>
                      <option value="false">غیرفعال</option>
                    </select>
                  </div>
                </div>
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">محتوا</label>
                  <textarea
                    name="content"
                    value={currentGuide?.content || ''}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="محتوا و توضیحات راهنما را وارد کنید..."
                  />
                </div>
                <div className="mt-6 flex justify-end space-x-2">
                  {editingGuide && (
                    <button
                      type="button"
                      onClick={cancelEditing}
                      className="ml-2 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      انصراف
                    </button>
                  )}
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {editingGuide ? 'به‌روزرسانی راهنما' : 'افزودن راهنما'}
                  </button>
                </div>
              </form>
            </div>
          )}
          
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">شناسه</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">عنوان</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تب</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ترتیب</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">وضعیت</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">عملیات</th>
                </tr>
              </thead>
              <tbody>
                {guides.map(guide => (
                  <tr key={guide._id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm">{guide._id.slice(-8)}</td>
                    <td className="py-3 px-4 text-sm max-w-xs truncate" title={guide.title}>
                      {guide.title}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {getTabLabel(guide.tab)}
                    </td>
                    <td className="py-3 px-4 text-sm">{guide.order}</td>
                    <td className="py-3 px-4 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        guide.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {guide.active ? 'فعال' : 'غیرفعال'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => toggleGuideStatus(guide._id)}
                          className={`ml-2 px-3 py-1 rounded text-xs ${
                            guide.active 
                              ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          {guide.active ? 'غیرفعال کردن' : 'فعال کردن'}
                        </button>
                        <button 
                          onClick={() => startEditing(guide._id)}
                          className="ml-2 px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
                        >
                          ویرایش
                        </button>
                        <button 
                          onClick={() => deleteGuide(guide._id)}
                          className="px-3 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200"
                        >
                          حذف
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
   
  );
};

export default Guide;
